import cn from 'classnames';
import { get } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Tabs.module.css';

export type TabItemProps<T> = {
  key: T;
  content: string;
  ariaLabel: string;
  icon?: JSX.Element;
};

export type TabItemChangeEvent<T> = {
  index: number;
  key: T;
};

export interface TabsProps<T> {
  items: TabItemProps<T>[];
  onItemClick: (event: TabItemChangeEvent<T>) => void;
  className?: string;
  defaultActiveIndex?: number;
}

enum TabsScrollPosition {
  None = 'none',
  Left = 'left',
  InBetween = 'inbetween',
  Right = 'right',
}

/**
 * Renders horizontal tabs like Fluent UI's Menu component, but without
 * iPad rendering issues and is optimized for smartphone use.
 */
export const TabsGeneric = <T,>({
  items,
  onItemClick,
  className,
  defaultActiveIndex = 0,
}: TabsProps<T>) => {
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<any>(
    get(items, `${defaultActiveIndex}.key`, items[0].key),
  );

  const [scrollPosition, setScrollPosition] = useState<TabsScrollPosition>(
    TabsScrollPosition.None,
  );

  const scrollObserverRef = useRef<ResizeObserver | null>(null);

  /**
   * Checks horizontal tabs scroll position on mobile and determines scrolling
   * position, so we can render box shadows to indicate in which directions the
   * user can scroll.
   */
  const checkScrollPosition = useCallback(() => {
    if (tabsRef === null || tabsRef.current === null) {
      return;
    }

    const hasHorizontalScrollbar =
      tabsRef.current.scrollWidth > tabsRef.current.clientWidth;
    const threshold = 10;

    if (!hasHorizontalScrollbar) {
      return setScrollPosition(TabsScrollPosition.None);
    }

    if (tabsRef.current.scrollLeft < threshold) {
      return setScrollPosition(TabsScrollPosition.Left);
    }

    if (
      tabsRef.current.scrollLeft + tabsRef.current.clientWidth >
      tabsRef.current.scrollWidth - threshold
    ) {
      return setScrollPosition(TabsScrollPosition.Right);
    }

    setScrollPosition(TabsScrollPosition.InBetween);
  }, []);

  /**
   * Attaches event handlers
   */
  useEffect(() => {
    const currentRef = tabsRef.current;
    if (!currentRef) return;

    if (!scrollObserverRef.current) {
      scrollObserverRef.current = new ResizeObserver(checkScrollPosition);
    }

    scrollObserverRef.current.observe(currentRef);
    currentRef.addEventListener('scroll', checkScrollPosition);

    return () => {
      currentRef.removeEventListener('scroll', checkScrollPosition);
      scrollObserverRef.current?.disconnect();
    };
  }, [checkScrollPosition]);

  const onTabClick = (key: T, index: number) => {
    setSelected(key);
    onItemClick({
      key,
      index,
    });
  };

  return (
    <div
      className={cn(styles.tabs, className, {
        [styles.boxShadowLeft]: scrollPosition === TabsScrollPosition.Right,
        [styles.boxShadowRight]: scrollPosition === TabsScrollPosition.Left,
        [styles.boxShadowLeftRight]:
          scrollPosition === TabsScrollPosition.InBetween,
      })}
      role="tablist"
      ref={tabsRef}
    >
      {items.map((item, index) => (
        <div
          key={index}
          tabIndex={0}
          className={cn(styles.tab, { [styles.active]: item.key === selected })}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onTabClick(item.key, index);
            }
          }}
          onClick={() => onTabClick(item.key, index)}
          role="tab"
          data-testid="tab-label"
          aria-selected={item.key === selected ? 'true' : 'false'}
          aria-label={item.ariaLabel}
          data-cy={get(item, 'data-cy')}
        >
          {item.content} {item.icon}
        </div>
      ))}
    </div>
  );
};

export const Tabs = TabsGeneric<string>;
